import { Activity } from "lucide-react"

interface Symptom {
  symptom_type: string
  count: number
}

interface SymptomFrequencyChartProps {
  symptoms: Symptom[]
  rangeLabel: string
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function SymptomFrequencyChart({
  symptoms,
  rangeLabel,
}: SymptomFrequencyChartProps) {
  const maxCount =
    symptoms.length > 0 ? Math.max(...symptoms.map((s) => s.count)) : 0

  return (
    <div
      className="insight-card rounded-2xl p-4"
      style={{
        background: "var(--fig-card-bg)",
        boxShadow: "var(--fig-card-shadow)",
        border: "1px solid var(--fig-card-border)",
      }}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700 }}>Symptom frequency</h3>
          <span style={{ fontSize: 12, color: "var(--fig-text-inactive)" }}>
            {rangeLabel}
          </span>
        </div>
        <div
          className="flex items-center justify-center"
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: "var(--fig-entry-symptom-bg)",
          }}
        >
          <Activity
            size={16}
            style={{ color: "var(--fig-entry-symptom-icon)" }}
          />
        </div>
      </div>

      {symptoms.length === 0 ? (
        <p
          style={{
            fontSize: 13,
            color: "var(--fig-text-inactive)",
            textAlign: "center",
            padding: "8px 0",
          }}
        >
          No symptoms logged yet
        </p>
      ) : (
        <div style={{ position: "relative" }}>
          {/* Y-axis guide lines */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              bottom: 40,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                borderBottom: "1px dashed var(--fig-divider-color)",
                opacity: 0.4,
              }}
            />
            <div
              style={{
                borderBottom: "1px dashed var(--fig-divider-color)",
                opacity: 0.4,
              }}
            />
            <div />
          </div>

          {/* Bars */}
          <div
            className="flex items-end gap-3"
            style={{ height: 120, position: "relative", zIndex: 1 }}
          >
            {symptoms.map(({ symptom_type, count }) => (
              <div key={symptom_type} className="flex flex-1 justify-center">
                <div
                  style={{
                    width: 36,
                    height:
                      maxCount > 0 ? `${(count / maxCount) * 100}%` : "4px",
                    minHeight: 4,
                    background: "var(--fig-bar-symptom-bg)",
                    borderRadius: "8px 8px 4px 4px",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Counts */}
          <div className="mt-1 flex gap-3">
            {symptoms.map(({ symptom_type, count }) => (
              <div key={symptom_type} className="flex-1 text-center">
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--fig-entry-symptom-icon)",
                  }}
                >
                  {count}
                </span>
              </div>
            ))}
          </div>

          {/* Labels */}
          <div className="mt-1 flex gap-3">
            {symptoms.map(({ symptom_type }) => (
              <div key={symptom_type} className="flex-1 text-center">
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--fig-text-primary)",
                  }}
                >
                  {capitalize(symptom_type)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
